import { useState, useEffect, useCallback } from "react";
import { Link } from "wouter";
import { NurseLayout } from "@/components/layout/nurse-layout";
import { PharmacistLayout } from "@/components/layout/pharmacist-layout";
import { DoctorLayout } from "@/components/layout/doctor-layout";
import { AdvicelineNurseLayout } from "@/components/layout/adviceline-nurse-layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Plus, Settings, X, ChevronRight, Pill, AlertTriangle,
  CheckCircle2, Clock, Loader2, Pencil, Trash2, User, Calendar,
  Building2, ClipboardList, Bell, Search, ClipboardPen, Circle, RotateCcw,
  Upload, Download, FileSpreadsheet,
} from "lucide-react";
import { useListMyPractices } from "@workspace/api-client-react";

const BASE_URL = import.meta.env.BASE_URL.replace(/\/$/, "");

type TrafficStatus = "red" | "amber" | "green" | "none";
type Role = "nurse" | "pharmacist" | "doctor";

interface UrgentMed { name: string; nextDueAt: string; daysUntil: number; }
interface PatientMedSummary { name: string; supplier: string | null; }
interface PatientSummary {
  id: number; rheumHubId: string; hospitalNumber: string; organisationName: string;
  firstName: string; lastName: string; dateOfBirth: string; diagnosis: string | null;
  notes: string | null; createdByUserId: string;
  status: TrafficStatus; medicationCount: number; urgentMedication: UrgentMed | null;
  medications: PatientMedSummary[];
}
interface FilterOptions { medications: string[]; suppliers: string[]; }
interface PrescriptionStep {
  id: number; stage: string; completedAt: string; completedByUserId: string;
  completedByName: string | null; completedByRole: string; notes: string | null;
}
interface PrescriptionStageDef { key: string; label: string; }
interface Medication {
  id: number; patientId: number; medicationName: string; medicationType: string | null;
  dose: string | null; frequency: string | null; supplier: string | null;
  lastPrescribedAt: string | null; nextDueAt: string; notes: string | null; isActive: boolean;
  prescriptionSteps?: PrescriptionStep[];
}
interface PatientDetail extends Omit<PatientSummary, "medications"> {
  medications: Medication[];
  prescriptionStages?: PrescriptionStageDef[];
}

const STATUS_COLORS: Record<TrafficStatus, string> = {
  red: "bg-red-500",
  amber: "bg-amber-400",
  green: "bg-green-500",
  none: "bg-slate-300",
};
const STATUS_BORDER: Record<TrafficStatus, string> = {
  red: "border-red-200 bg-red-50",
  amber: "border-amber-200 bg-amber-50",
  green: "border-green-200 bg-green-50",
  none: "border-slate-200 bg-white",
};
const STATUS_LABEL: Record<TrafficStatus, string> = {
  red: "Overdue",
  amber: "Due Soon",
  green: "On Track",
  none: "No Medications",
};

function formatDate(d: string): string {
  return new Date(d + "T00:00:00").toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}
function daysUntil(dateStr: string): number {
  const t = new Date();
  t.setHours(0, 0, 0, 0);
  const due = new Date(dateStr + "T00:00:00");
  due.setHours(0, 0, 0, 0);
  return Math.round((due.getTime() - t.getTime()) / 86_400_000);
}
function daysLabel(days: number): string {
  if (days < 0) return `${Math.abs(days)} day${Math.abs(days) !== 1 ? "s" : ""} overdue`;
  if (days === 0) return "Due today";
  return `Due in ${days} day${days !== 1 ? "s" : ""}`;
}
function today(): string {
  return new Date().toISOString().slice(0, 10);
}

// ─── Prescription progress checklist ──────────────────────────────────────────
const STAGE_LABEL_FALLBACK: Record<string, string> = {
  requested: "Prescription requested",
  pharmacist_ack: "Acknowledged by pharmacist",
  forwarded_to_supplier: "Forwarded to supplier",
  supplier_ack: "Acknowledged by supplier",
  patient_notified: "Patient notified to contact supplier",
};
const DEFAULT_STAGE_ORDER: PrescriptionStageDef[] = [
  { key: "requested", label: STAGE_LABEL_FALLBACK["requested"] },
  { key: "pharmacist_ack", label: STAGE_LABEL_FALLBACK["pharmacist_ack"] },
  { key: "forwarded_to_supplier", label: STAGE_LABEL_FALLBACK["forwarded_to_supplier"] },
  { key: "supplier_ack", label: STAGE_LABEL_FALLBACK["supplier_ack"] },
  { key: "patient_notified", label: STAGE_LABEL_FALLBACK["patient_notified"] },
];

function formatStepTimestamp(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

function PrescriptionChecklist({
  stages, steps, onMark, onUnmark, onReset,
}: {
  stages: PrescriptionStageDef[];
  steps: PrescriptionStep[];
  onMark: (stage: string, notes?: string) => void | Promise<void>;
  onUnmark: (stage: string) => void | Promise<void>;
  onReset: () => void | Promise<void>;
}) {
  const [noteDraft, setNoteDraft] = useState<{ stage: string; value: string } | null>(null);
  const stepByStage = new Map(steps.map((s) => [s.stage, s]));
  const completedCount = stages.filter((s) => stepByStage.has(s.key)).length;

  return (
    <div className="mt-3 border-t border-slate-200/60 pt-3">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <ClipboardList className="h-3.5 w-3.5 text-slate-500" />
          <span className="text-xs font-semibold text-slate-700">Prescription progress</span>
          <span className="text-xs text-slate-400">{completedCount}/{stages.length}</span>
        </div>
        {completedCount > 0 && (
          <button
            type="button"
            onClick={() => onReset()}
            className="text-[11px] font-medium text-slate-500 hover:text-slate-800 flex items-center gap-1"
            title="Clear all ticks to start a new prescription cycle"
          >
            <RotateCcw className="h-3 w-3" /> Reset cycle
          </button>
        )}
      </div>
      <ol className="space-y-1.5">
        {stages.map((stage) => {
          const step = stepByStage.get(stage.key);
          const done = Boolean(step);
          const editing = noteDraft?.stage === stage.key;
          return (
            <li key={stage.key} className="text-xs">
              <div className="flex items-start gap-2">
                <button
                  type="button"
                  onClick={() =>
                    done ? onUnmark(stage.key) : onMark(stage.key)
                  }
                  className="mt-0.5 shrink-0"
                  aria-label={done ? `Un-mark ${stage.label}` : `Mark ${stage.label} as done`}
                  title={done ? "Click to un-mark (mistake)" : "Click to mark as done"}
                >
                  {done ? (
                    <CheckCircle2 className="h-4 w-4 text-teal-600" />
                  ) : (
                    <Circle className="h-4 w-4 text-slate-300 hover:text-slate-500 transition-colors" />
                  )}
                </button>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className={done ? "text-slate-800 font-medium" : "text-slate-500"}>{stage.label}</span>
                    {step && (
                      <span className="text-[11px] text-slate-400">
                        {formatStepTimestamp(step.completedAt)}
                        {step.completedByName ? ` · ${step.completedByName}` : ""}
                        {step.completedByRole ? ` (${step.completedByRole})` : ""}
                      </span>
                    )}
                  </div>
                  {step?.notes && (
                    <p className="mt-0.5 text-[11px] text-slate-500 italic">"{step.notes}"</p>
                  )}
                  {!editing && (
                    <button
                      type="button"
                      onClick={() => setNoteDraft({ stage: stage.key, value: step?.notes ?? "" })}
                      className="mt-0.5 text-[11px] text-teal-700 hover:text-teal-900 font-medium"
                    >
                      {step?.notes ? "Edit note" : done ? "+ Add note" : "+ Mark done with note"}
                    </button>
                  )}
                  {editing && (
                    <div className="mt-1 flex items-start gap-1.5">
                      <Input
                        autoFocus
                        value={noteDraft.value}
                        onChange={(e) => setNoteDraft({ stage: stage.key, value: e.target.value })}
                        placeholder="e.g. Supplier confirmed by phone"
                        className="h-7 text-xs"
                      />
                      <Button
                        type="button"
                        size="sm"
                        onClick={async () => {
                          await onMark(stage.key, noteDraft.value.trim() || undefined);
                          setNoteDraft(null);
                        }}
                        className="h-7 px-2 text-xs bg-teal-700 hover:bg-teal-800 text-white"
                      >
                        Save
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => setNoteDraft(null)}
                        className="h-7 px-2 text-xs"
                      >
                        Cancel
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

// ─── Bulk import modal ────────────────────────────────────────────────────────
interface ImportRow {
  rowIndex: number;
  hospitalNumber: string;
  firstName: string;
  lastName: string;
  dateOfBirth: string | null;
  medication: { medicationName: string; nextDueAt: string | null } | null;
  errors: string[];
}
interface ImportSummary {
  totalRows: number;
  validRows: number;
  errorRows: number;
  newPatients: number;
  existingPatients: number;
  medicationsToAdd: number;
  createdPatients?: number;
  createdMedications?: number;
}

function ImportModal({ onClose, onCommitted }: { onClose: () => void; onCommitted: () => Promise<void> }) {
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState<"idle" | "previewing" | "committing">("idle");
  const [preview, setPreview] = useState<{ summary: ImportSummary; rows: ImportRow[] } | null>(null);
  const [result, setResult] = useState<ImportSummary | null>(null);
  const [topError, setTopError] = useState("");

  const downloadTemplate = async () => {
    const res = await fetch(`${BASE_URL}/api/med-tracker/import/template`, { credentials: "include" });
    if (!res.ok) { setTopError("Couldn't download the template — try again."); return; }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "med-tracker-import-template.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const runPreview = async (f: File) => {
    setBusy("previewing");
    setTopError("");
    setPreview(null);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const res = await fetch(`${BASE_URL}/api/med-tracker/import?dryRun=1`, {
        method: "POST", credentials: "include", body: fd,
      });
      const data = await res.json();
      if (!res.ok) { setTopError(data.error ?? "Couldn't read the file."); return; }
      setPreview({ summary: data.summary, rows: data.rows });
    } finally {
      setBusy("idle");
    }
  };

  const runCommit = async () => {
    if (!file) return;
    setBusy("committing");
    setTopError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(`${BASE_URL}/api/med-tracker/import`, {
        method: "POST", credentials: "include", body: fd,
      });
      const data = await res.json();
      if (!res.ok) {
        setTopError(data.error ?? "Import failed.");
        if (data.summary) setPreview({ summary: data.summary, rows: data.rows ?? [] });
        return;
      }
      setResult(data.summary);
    } finally {
      setBusy("idle");
    }
  };

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0] ?? null;
    setFile(f);
    setPreview(null);
    setResult(null);
    setTopError("");
    if (f) void runPreview(f);
  };

  // Success state
  if (result) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-3 rounded-xl bg-green-50 border border-green-200 p-4">
          <CheckCircle2 className="h-6 w-6 text-green-700 shrink-0" />
          <div className="text-sm">
            <p className="font-semibold text-green-900">Import complete</p>
            <p className="text-green-800">
              Added {result.createdPatients ?? 0} new patient{(result.createdPatients ?? 0) !== 1 ? "s" : ""}
              {" and "}
              {result.createdMedications ?? 0} medication{(result.createdMedications ?? 0) !== 1 ? "s" : ""}.
              {result.existingPatients > 0 && ` ${result.existingPatients} patient${result.existingPatients !== 1 ? "s" : ""} were already in your organisation — their medications were added to the existing record.`}
            </p>
          </div>
        </div>
        <div className="flex justify-end">
          <Button onClick={() => void onCommitted()} className="bg-teal-700 hover:bg-teal-800 text-white">Done</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Step 1: template */}
      <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
        <div className="flex items-start gap-3">
          <FileSpreadsheet className="h-5 w-5 text-slate-600 shrink-0 mt-0.5" />
          <div className="flex-1 text-sm">
            <p className="font-medium text-slate-900">1. Start with the template</p>
            <p className="text-slate-600 mt-0.5">
              Download the template, paste your existing data in, then upload it back. One row per medication —
              if a patient is on 3 drugs, use 3 rows with the same hospital number.
            </p>
          </div>
          <Button variant="outline" onClick={downloadTemplate} className="gap-1.5 shrink-0">
            <Download className="h-4 w-4" /> Template
          </Button>
        </div>
      </div>

      {/* Step 2: file picker */}
      <div>
        <Label className="text-sm font-medium">2. Choose your file (CSV or Excel, max 5 MB)</Label>
        <input
          type="file"
          accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
          onChange={onFileChange}
          disabled={busy !== "idle"}
          className="mt-1.5 block w-full text-sm text-slate-600 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-teal-50 file:text-teal-700 hover:file:bg-teal-100"
        />
        {file && <p className="mt-1 text-xs text-slate-500">{file.name} · {(file.size / 1024).toFixed(1)} KB</p>}
      </div>

      {topError && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800 flex items-start gap-2">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" /> {topError}
        </div>
      )}

      {busy === "previewing" && (
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin" /> Reading file…
        </div>
      )}

      {/* Step 3: preview */}
      {preview && (
        <div className="space-y-3">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-center">
            <div className="rounded-lg bg-slate-50 border border-slate-200 p-2">
              <p className="text-xs text-slate-500">Total rows</p>
              <p className="text-lg font-bold text-slate-900">{preview.summary.totalRows}</p>
            </div>
            <div className="rounded-lg bg-teal-50 border border-teal-200 p-2">
              <p className="text-xs text-teal-700">New patients</p>
              <p className="text-lg font-bold text-teal-900">{preview.summary.newPatients}</p>
            </div>
            <div className="rounded-lg bg-blue-50 border border-blue-200 p-2">
              <p className="text-xs text-blue-700">Medications to add</p>
              <p className="text-lg font-bold text-blue-900">{preview.summary.medicationsToAdd}</p>
            </div>
            <div className={`rounded-lg border p-2 ${preview.summary.errorRows > 0 ? "bg-red-50 border-red-200" : "bg-slate-50 border-slate-200"}`}>
              <p className={`text-xs ${preview.summary.errorRows > 0 ? "text-red-700" : "text-slate-500"}`}>Errors</p>
              <p className={`text-lg font-bold ${preview.summary.errorRows > 0 ? "text-red-900" : "text-slate-900"}`}>{preview.summary.errorRows}</p>
            </div>
          </div>

          {preview.summary.existingPatients > 0 && (
            <p className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-lg p-2.5">
              {preview.summary.existingPatients} hospital number{preview.summary.existingPatients !== 1 ? "s" : ""} already exist in your organisation. New medications from those rows will be added to the existing patient record.
            </p>
          )}

          <div className="max-h-72 overflow-y-auto border border-slate-200 rounded-xl">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 sticky top-0">
                <tr className="text-left text-slate-600">
                  <th className="px-2 py-1.5 font-medium w-10">Row</th>
                  <th className="px-2 py-1.5 font-medium">Patient</th>
                  <th className="px-2 py-1.5 font-medium">Hospital #</th>
                  <th className="px-2 py-1.5 font-medium">Medication</th>
                  <th className="px-2 py-1.5 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {preview.rows.map((r) => (
                  <tr key={r.rowIndex} className={`border-t border-slate-100 ${r.errors.length > 0 ? "bg-red-50" : ""}`}>
                    <td className="px-2 py-1.5 text-slate-500">{r.rowIndex}</td>
                    <td className="px-2 py-1.5 text-slate-800">{r.firstName} {r.lastName}</td>
                    <td className="px-2 py-1.5 text-slate-600">{r.hospitalNumber}</td>
                    <td className="px-2 py-1.5 text-slate-600">{r.medication?.medicationName ?? <span className="text-slate-400">—</span>}</td>
                    <td className="px-2 py-1.5">
                      {r.errors.length === 0 ? (
                        <span className="inline-flex items-center gap-1 text-green-700"><CheckCircle2 className="h-3 w-3" /> OK</span>
                      ) : (
                        <span className="text-red-700">{r.errors.join("; ")}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button
              onClick={runCommit}
              disabled={busy !== "idle" || preview.summary.errorRows > 0 || preview.summary.validRows === 0}
              className="bg-teal-700 hover:bg-teal-800 text-white"
            >
              {busy === "committing" && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {preview.summary.errorRows > 0
                ? "Fix errors above first"
                : `Import ${preview.summary.newPatients} patient${preview.summary.newPatients !== 1 ? "s" : ""} + ${preview.summary.medicationsToAdd} medication${preview.summary.medicationsToAdd !== 1 ? "s" : ""}`}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Modal wrapper ────────────────────────────────────────────────────────────
function Modal({ title, onClose, children, wide }: { title: string; onClose: () => void; children: React.ReactNode; wide?: boolean }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className={`bg-white rounded-2xl shadow-xl w-full ${wide ? "max-w-2xl" : "max-w-lg"} max-h-[90vh] overflow-y-auto`}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 sticky top-0 bg-white z-10">
          <h2 className="font-bold text-slate-900 text-lg">{title}</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 transition-colors"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}

// ─── Patient form ─────────────────────────────────────────────────────────────
interface PatientFormData { hospitalNumber: string; firstName: string; lastName: string; dateOfBirth: string; diagnosis: string; notes: string; }
function PatientForm({ initial, onSave, onCancel, saving, error }: {
  initial?: Partial<PatientFormData>; onSave: (d: PatientFormData) => void;
  onCancel: () => void; saving: boolean; error: string;
}) {
  const [form, setForm] = useState<PatientFormData>({
    hospitalNumber: initial?.hospitalNumber ?? "",
    firstName: initial?.firstName ?? "",
    lastName: initial?.lastName ?? "",
    dateOfBirth: initial?.dateOfBirth ?? "",
    diagnosis: initial?.diagnosis ?? "",
    notes: initial?.notes ?? "",
  });
  const set = (k: keyof PatientFormData) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm(f => ({ ...f, [k]: e.target.value }));
  const valid = form.hospitalNumber.trim() && form.firstName.trim() && form.lastName.trim() && form.dateOfBirth;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label className="text-sm font-medium">First name *</Label>
          <Input value={form.firstName} onChange={set("firstName")} placeholder="Jane" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-sm font-medium">Last name *</Label>
          <Input value={form.lastName} onChange={set("lastName")} placeholder="Smith" />
        </div>
      </div>
      <div className="space-y-1.5">
        <Label className="text-sm font-medium">Date of birth *</Label>
        <Input type="date" value={form.dateOfBirth} onChange={set("dateOfBirth")} max={today()} />
      </div>
      <div className="space-y-1.5">
        <Label className="text-sm font-medium">Hospital number *</Label>
        <Input value={form.hospitalNumber} onChange={set("hospitalNumber")} placeholder="e.g. TH-123456" />
        <p className="text-xs text-slate-500">Used to prevent duplicate records across your team</p>
      </div>
      <div className="space-y-1.5">
        <Label className="text-sm font-medium">Diagnosis <span className="text-slate-400 font-normal">(optional)</span></Label>
        <Input value={form.diagnosis} onChange={set("diagnosis")} placeholder="e.g. Rheumatoid Arthritis" />
      </div>
      <div className="space-y-1.5">
        <Label className="text-sm font-medium">Notes <span className="text-slate-400 font-normal">(optional)</span></Label>
        <textarea
          value={form.notes} onChange={set("notes")} rows={2}
          placeholder="Any additional notes..."
          className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 resize-none focus:outline-none focus:ring-2 focus:ring-teal-500"
        />
      </div>
      {error && <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</p>}
      <div className="flex justify-end gap-2 pt-2">
        <Button variant="outline" onClick={onCancel}>Cancel</Button>
        <Button onClick={() => onSave(form)} disabled={!valid || saving} className="bg-teal-700 hover:bg-teal-800 text-white">
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
          Save patient
        </Button>
      </div>
    </div>
  );
}

// ─── Medication form ──────────────────────────────────────────────────────────
const MED_TYPES = ["Biologic (bDMARD)", "Conventional DMARD (csDMARD)", "Targeted DMARD (tsDMARD)", "Corticosteroid", "Analgesic / NSAID", "Other"];
const FREQUENCIES = ["Every 2 weeks", "Every 4 weeks", "Monthly", "Every 6 weeks", "Every 8 weeks", "Every 3 months", "Every 6 months", "Annual", "Other"];
const SUPPLIERS = ["Sciensus (Healthcare at Home)", "LloydsPharmacy Clinical Homecare", "Iqvia Biotech", "Clinigen", "Hospital pharmacy", "Other"];

interface MedFormData { medicationName: string; medicationType: string; dose: string; frequency: string; supplier: string; lastPrescribedAt: string; nextDueAt: string; notes: string; isActive: boolean; }
function MedForm({ initial, onSave, onCancel, saving, error }: {
  initial?: Partial<MedFormData>; onSave: (d: MedFormData) => void;
  onCancel: () => void; saving: boolean; error: string;
}) {
  const [form, setForm] = useState<MedFormData>({
    medicationName: initial?.medicationName ?? "",
    medicationType: initial?.medicationType ?? "",
    dose: initial?.dose ?? "",
    frequency: initial?.frequency ?? "",
    supplier: initial?.supplier ?? "",
    lastPrescribedAt: initial?.lastPrescribedAt ?? "",
    nextDueAt: initial?.nextDueAt ?? "",
    notes: initial?.notes ?? "",
    isActive: initial?.isActive ?? true,
  });
  const set = (k: keyof MedFormData) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    setForm(f => ({ ...f, [k]: e.target.value }));
  const valid = form.medicationName.trim() && form.nextDueAt;
  return (
    <div className="space-y-4">
      <div className="space-y-1.5">
        <Label className="text-sm font-medium">Medication name *</Label>
        <Input value={form.medicationName} onChange={set("medicationName")} placeholder="e.g. Adalimumab (Humira)" />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label className="text-sm font-medium">Type</Label>
          <select value={form.medicationType} onChange={set("medicationType")} className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500">
            <option value="">Select type...</option>
            {MED_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-sm font-medium">Dose</Label>
          <Input value={form.dose} onChange={set("dose")} placeholder="e.g. 40mg" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label className="text-sm font-medium">Frequency</Label>
          <select value={form.frequency} onChange={set("frequency")} className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500">
            <option value="">Select frequency...</option>
            {FREQUENCIES.map(f => <option key={f} value={f}>{f}</option>)}
          </select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-sm font-medium">Supplier</Label>
          <select value={form.supplier} onChange={set("supplier")} className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500">
            <option value="">Select supplier...</option>
            {SUPPLIERS.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label className="text-sm font-medium">Last prescribed</Label>
          <Input type="date" value={form.lastPrescribedAt} onChange={set("lastPrescribedAt")} />
        </div>
        <div className="space-y-1.5">
          <Label className="text-sm font-medium">Next due date *</Label>
          <Input type="date" value={form.nextDueAt} onChange={set("nextDueAt")} />
        </div>
      </div>
      <div className="space-y-1.5">
        <Label className="text-sm font-medium">Notes</Label>
        <textarea value={form.notes} onChange={set("notes")} rows={2} placeholder="Any additional notes..." className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-teal-500" />
      </div>
      <label className="flex items-center gap-2 cursor-pointer">
        <input type="checkbox" checked={form.isActive} onChange={e => setForm(f => ({ ...f, isActive: e.target.checked }))} className="h-4 w-4 rounded" />
        <span className="text-sm text-slate-700">Active prescription</span>
      </label>
      {error && <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</p>}
      <div className="flex justify-end gap-2 pt-2">
        <Button variant="outline" onClick={onCancel}>Cancel</Button>
        <Button onClick={() => onSave(form)} disabled={!valid || saving} className="bg-teal-700 hover:bg-teal-800 text-white">
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
          Save medication
        </Button>
      </div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────
export default function MedTrackerPage() {
  const role = (localStorage.getItem("rheumhub_role") as Role) ?? "nurse";

  const [patients, setPatients] = useState<PatientSummary[]>([]);
  const [alertDays, setAlertDays] = useState(14);
  const [filterOptions, setFilterOptions] = useState<FilterOptions>({ medications: [], suppliers: [] });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<"all" | TrafficStatus>("all");
  const [search, setSearch] = useState("");
  const [medicationFilter, setMedicationFilter] = useState("");
  const [supplierFilter, setSupplierFilter] = useState("");

  const [showAddPatient, setShowAddPatient] = useState(false);
  const [editPatient, setEditPatient] = useState<PatientSummary | PatientDetail | null>(null);
  const [patientError, setPatientError] = useState("");
  const [patientSaving, setPatientSaving] = useState(false);

  const [selectedPatient, setSelectedPatient] = useState<PatientDetail | null>(null);
  const [patientDetailLoading, setPatientDetailLoading] = useState(false);

  const [showAddMed, setShowAddMed] = useState(false);
  const [editMed, setEditMed] = useState<Medication | null>(null);
  const [medError, setMedError] = useState("");
  const [medSaving, setMedSaving] = useState(false);

  const [showSettings, setShowSettings] = useState(false);
  const [showImport, setShowImport] = useState(false);

  // Active practice context — used to decide whether to surface the
  // "Open clinical record" link. NHS practices rely on the EPR for the
  // patient record, so the link is hidden there. Private and independent
  // clinics typically have no EPR, so they need it.
  const { data: practices } = useListMyPractices();
  const activePractice = (practices ?? []).find((p) => p.isActive) ?? null;
  const showClinicalRecordLink = activePractice?.practiceType === "private"
    || activePractice?.practiceType === "independent";
  const [settingsDays, setSettingsDays] = useState(14);
  const [settingsSaving, setSettingsSaving] = useState(false);

  const loadPatients = useCallback(async () => {
    try {
      const res = await fetch(`${BASE_URL}/api/med-tracker/patients`, { credentials: "include" });
      if (!res.ok) return;
      const data = await res.json() as { patients: PatientSummary[]; alertDaysAdvance: number; filterOptions: FilterOptions };
      setPatients(data.patients);
      setAlertDays(data.alertDaysAdvance);
      setSettingsDays(data.alertDaysAdvance);
      setFilterOptions(data.filterOptions ?? { medications: [], suppliers: [] });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadPatients(); }, [loadPatients]);

  const loadPatientDetail = async (id: number) => {
    setPatientDetailLoading(true);
    try {
      const res = await fetch(`${BASE_URL}/api/med-tracker/patients/${id}`, { credentials: "include" });
      if (res.ok) setSelectedPatient(await res.json() as PatientDetail);
    } finally {
      setPatientDetailLoading(false);
    }
  };

  const handleAddPatient = async (form: PatientFormData) => {
    setPatientSaving(true);
    setPatientError("");
    try {
      const res = await fetch(`${BASE_URL}/api/med-tracker/patients`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (res.status === 409) {
        setPatientError(data.message ?? "A patient with this hospital number already exists.");
        return;
      }
      if (!res.ok) { setPatientError(data.error ?? "Failed to save patient"); return; }
      setShowAddPatient(false);
      await loadPatients();
    } finally {
      setPatientSaving(false);
    }
  };

  const handleEditPatient = async (form: PatientFormData) => {
    if (!editPatient) return;
    setPatientSaving(true);
    setPatientError("");
    try {
      const res = await fetch(`${BASE_URL}/api/med-tracker/patients/${editPatient.id}`, {
        method: "PUT", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) { setPatientError(data.error ?? "Failed to update patient"); return; }
      setEditPatient(null);
      await loadPatients();
      if (selectedPatient?.id === editPatient.id) await loadPatientDetail(editPatient.id);
    } finally {
      setPatientSaving(false);
    }
  };

  const handleDeletePatient = async (id: number) => {
    if (!confirm("Delete this patient and all their medication records? This cannot be undone.")) return;
    await fetch(`${BASE_URL}/api/med-tracker/patients/${id}`, { method: "DELETE", credentials: "include" });
    setSelectedPatient(null);
    await loadPatients();
  };

  const handleAddMed = async (form: MedFormData) => {
    if (!selectedPatient) return;
    setMedSaving(true);
    setMedError("");
    try {
      const res = await fetch(`${BASE_URL}/api/med-tracker/patients/${selectedPatient.id}/medications`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) { const d = await res.json(); setMedError(d.error ?? "Failed to save medication"); return; }
      setShowAddMed(false);
      await loadPatientDetail(selectedPatient.id);
      await loadPatients();
    } finally {
      setMedSaving(false);
    }
  };

  const handleEditMed = async (form: MedFormData) => {
    if (!editMed || !selectedPatient) return;
    setMedSaving(true);
    setMedError("");
    try {
      const res = await fetch(`${BASE_URL}/api/med-tracker/medications/${editMed.id}`, {
        method: "PUT", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) { const d = await res.json(); setMedError(d.error ?? "Failed to update medication"); return; }
      setEditMed(null);
      await loadPatientDetail(selectedPatient.id);
      await loadPatients();
    } finally {
      setMedSaving(false);
    }
  };

  const handleDeleteMed = async (medId: number) => {
    if (!selectedPatient) return;
    if (!confirm("Remove this medication?")) return;
    await fetch(`${BASE_URL}/api/med-tracker/medications/${medId}`, { method: "DELETE", credentials: "include" });
    await loadPatientDetail(selectedPatient.id);
    await loadPatients();
  };

  // ─── Prescription progress step handlers ──────────────────────────────────
  const handleMarkStep = async (medId: number, stage: string, notes?: string) => {
    if (!selectedPatient) return;
    await fetch(`${BASE_URL}/api/med-tracker/medications/${medId}/prescription-steps`, {
      method: "POST", credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ stage, notes }),
    });
    await loadPatientDetail(selectedPatient.id);
  };

  const handleUnmarkStep = async (medId: number, stage: string) => {
    if (!selectedPatient) return;
    await fetch(`${BASE_URL}/api/med-tracker/medications/${medId}/prescription-steps/${stage}`, {
      method: "DELETE", credentials: "include",
    });
    await loadPatientDetail(selectedPatient.id);
  };

  const handleResetCycle = async (medId: number) => {
    if (!selectedPatient) return;
    if (!confirm("Reset prescription progress? This clears every tick so a new cycle can start.")) return;
    await fetch(`${BASE_URL}/api/med-tracker/medications/${medId}/prescription-steps/reset`, {
      method: "POST", credentials: "include",
    });
    await loadPatientDetail(selectedPatient.id);
  };

  const handleSaveSettings = async () => {
    setSettingsSaving(true);
    try {
      await fetch(`${BASE_URL}/api/med-tracker/settings`, {
        method: "PUT", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ alertDaysAdvance: settingsDays }),
      });
      setAlertDays(settingsDays);
      setShowSettings(false);
      await loadPatients();
    } finally {
      setSettingsSaving(false);
    }
  };

  const searchTerm = search.trim().toLowerCase();
  const matchesAll = (p: PatientSummary) => {
    if (searchTerm) {
      const textMatch =
        `${p.firstName} ${p.lastName}`.toLowerCase().includes(searchTerm) ||
        p.hospitalNumber.toLowerCase().includes(searchTerm) ||
        (p.diagnosis ?? "").toLowerCase().includes(searchTerm) ||
        p.medications.some(m => m.name.toLowerCase().includes(searchTerm));
      if (!textMatch) return false;
    }
    if (medicationFilter) {
      if (!p.medications.some(m => m.name === medicationFilter)) return false;
    }
    if (supplierFilter) {
      if (!p.medications.some(m => m.supplier === supplierFilter)) return false;
    }
    return true;
  };
  const statusFiltered = filter === "all" ? patients : patients.filter(p => p.status === filter);
  const filtered = statusFiltered.filter(matchesAll);
  const counts = {
    all: patients.filter(matchesAll).length,
    red: patients.filter(p => p.status === "red" && matchesAll(p)).length,
    amber: patients.filter(p => p.status === "amber" && matchesAll(p)).length,
    green: patients.filter(p => p.status === "green" && matchesAll(p)).length,
  };
  const activeFilterCount = (search ? 1 : 0) + (medicationFilter ? 1 : 0) + (supplierFilter ? 1 : 0);
  const clearAllFilters = () => { setSearch(""); setMedicationFilter(""); setSupplierFilter(""); };

  const content = (
    <div className="space-y-6">
      {/* Alert summary */}
      {(counts.red > 0 || counts.amber > 0) && (
        <div className={`flex items-start gap-3 rounded-xl px-4 py-3 border ${counts.red > 0 ? "bg-red-50 border-red-200 text-red-800" : "bg-amber-50 border-amber-200 text-amber-800"}`}>
          <Bell className="h-4 w-4 shrink-0 mt-0.5" />
          <p className="text-sm">
            {counts.red > 0 && <span className="font-semibold">{counts.red} overdue prescription{counts.red !== 1 ? "s" : ""}. </span>}
            {counts.amber > 0 && <span>{counts.amber} prescription{counts.amber !== 1 ? "s" : ""} due within {alertDays} days.</span>}
          </p>
        </div>
      )}

      {/* Controls */}
      <div className="space-y-3">
        {/* Row 1: search + dropdowns */}
        <div className="flex gap-2 flex-wrap">
          {/* Text search */}
          <div className="relative flex-1 min-w-48">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
            <input
              type="search"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Name, hospital no., diagnosis…"
              className="w-full pl-9 pr-8 py-2.5 rounded-xl border border-slate-200 bg-white text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500"
              aria-label="Search patients"
            />
            {search && (
              <button type="button" aria-label="Clear search" onClick={() => setSearch("")}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors">
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>

          {/* Medication dropdown */}
          <div className="relative min-w-44">
            <Pill className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
            <select
              value={medicationFilter}
              onChange={e => setMedicationFilter(e.target.value)}
              aria-label="Filter by medication"
              className={`w-full appearance-none pl-9 pr-8 py-2.5 rounded-xl border text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 ${medicationFilter ? "border-teal-500 bg-teal-50 text-teal-800 font-medium" : "border-slate-200 bg-white text-slate-600"}`}
            >
              <option value="">All medications</option>
              {filterOptions.medications.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
            {medicationFilter && (
              <button type="button" aria-label="Clear medication filter" onClick={() => setMedicationFilter("")}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-teal-600 hover:text-teal-800 transition-colors">
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>

          {/* Supplier dropdown */}
          <div className="relative min-w-44">
            <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
            <select
              value={supplierFilter}
              onChange={e => setSupplierFilter(e.target.value)}
              aria-label="Filter by supplier"
              className={`w-full appearance-none pl-9 pr-8 py-2.5 rounded-xl border text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 ${supplierFilter ? "border-teal-500 bg-teal-50 text-teal-800 font-medium" : "border-slate-200 bg-white text-slate-600"}`}
            >
              <option value="">All suppliers</option>
              {filterOptions.suppliers.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            {supplierFilter && (
              <button type="button" aria-label="Clear supplier filter" onClick={() => setSupplierFilter("")}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-teal-600 hover:text-teal-800 transition-colors">
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </div>

        {/* Active filter chips */}
        {activeFilterCount > 0 && (
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-slate-500">Filtered by:</span>
            {search && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-slate-100 text-slate-700 text-xs font-medium">
                "{search}"
                <button type="button" aria-label="Remove text search" onClick={() => setSearch("")}><X className="h-3 w-3" /></button>
              </span>
            )}
            {medicationFilter && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-teal-100 text-teal-800 text-xs font-medium">
                <Pill className="h-3 w-3" /> {medicationFilter}
                <button type="button" aria-label="Remove medication filter" onClick={() => setMedicationFilter("")}><X className="h-3 w-3" /></button>
              </span>
            )}
            {supplierFilter && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-teal-100 text-teal-800 text-xs font-medium">
                <Building2 className="h-3 w-3" /> {supplierFilter}
                <button type="button" aria-label="Remove supplier filter" onClick={() => setSupplierFilter("")}><X className="h-3 w-3" /></button>
              </span>
            )}
            {activeFilterCount > 1 && (
              <button type="button" onClick={clearAllFilters} className="text-xs text-slate-500 hover:text-slate-700 underline underline-offset-2">
                Clear all
              </button>
            )}
          </div>
        )}

        {/* Row 2: traffic light tabs + action buttons */}
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex gap-1.5 flex-wrap">
            {(["all", "red", "amber", "green"] as const).map(f => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${filter === f ? "bg-slate-800 text-white border-slate-800" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}
              >
                {f !== "all" && <span className={`h-2.5 w-2.5 rounded-full ${STATUS_COLORS[f]}`} />}
                {f === "all" ? `All (${counts.all})` : `${STATUS_LABEL[f]} (${counts[f]})`}
              </button>
            ))}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setShowImport(true)} className="gap-1.5 text-slate-600">
              <Upload className="h-4 w-4" /> Import
            </Button>
            <Button variant="outline" onClick={() => setShowSettings(true)} className="gap-1.5 text-slate-600">
              <Settings className="h-4 w-4" /> Alert: {alertDays}d
            </Button>
            <Button onClick={() => { setShowAddPatient(true); setPatientError(""); }} className="bg-teal-700 hover:bg-teal-800 text-white gap-1.5">
              <Plus className="h-4 w-4" /> Add patient
            </Button>
          </div>
        </div>
      </div>

      {/* Patient list */}
      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-slate-300" /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-slate-400">
          <ClipboardList className="h-12 w-12 mx-auto mb-3 opacity-30" />
          <p className="font-medium text-slate-500">
            {patients.length === 0
              ? "No patients added yet"
              : searchTerm
              ? `No patients match "${search}"`
              : "No patients match this filter"}
          </p>
          {patients.length === 0 && <p className="text-sm mt-1">Add your first patient to start tracking prescriptions</p>}
          {patients.length > 0 && searchTerm && (
            <button type="button" onClick={() => setSearch("")} className="mt-2 text-sm text-teal-700 hover:underline">
              Clear search
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(p => (
            <button
              key={p.id}
              type="button"
              className={`w-full text-left rounded-xl border p-4 hover:shadow-sm transition-shadow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 ${STATUS_BORDER[p.status]}`}
              onClick={() => { loadPatientDetail(p.id); }}
              aria-label={`View details for ${p.firstName} ${p.lastName}, hospital number ${p.hospitalNumber}`}
            >
              <div className="flex items-center gap-3">
                <div className={`h-3 w-3 rounded-full shrink-0 ${STATUS_COLORS[p.status]}`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className="font-semibold text-slate-900">{p.firstName} {p.lastName}</span>
                    <span className="text-xs text-slate-500">DOB: {formatDate(p.dateOfBirth)}</span>
                    <span className="text-xs text-slate-400">#{p.hospitalNumber}</span>
                    {p.diagnosis && <span className="text-xs text-slate-500 italic">{p.diagnosis}</span>}
                  </div>
                  {p.urgentMedication ? (
                    <p className={`text-sm mt-0.5 ${p.status === "red" ? "text-red-700 font-medium" : p.status === "amber" ? "text-amber-700" : "text-slate-500"}`}>
                      {p.urgentMedication.name} — {daysLabel(p.urgentMedication.daysUntil)}
                    </p>
                  ) : (
                    <p className="text-sm mt-0.5 text-slate-400">{p.medicationCount === 0 ? "No medications added" : `${p.medicationCount} medication${p.medicationCount !== 1 ? "s" : ""}`}</p>
                  )}
                </div>
                <ChevronRight className="h-4 w-4 text-slate-300 shrink-0" />
              </div>
            </button>
          ))}
        </div>
      )}

      {/* ── Patient detail panel ── */}
      {(selectedPatient || patientDetailLoading) && (
        <div className="fixed inset-0 z-40 flex">
          <button
            type="button"
            aria-label="Close patient detail panel"
            className="flex-1 bg-black/30 cursor-default"
            onClick={() => setSelectedPatient(null)}
          />
          <div className="w-full max-w-xl bg-white shadow-2xl overflow-y-auto flex flex-col">
            {patientDetailLoading ? (
              <div className="flex-1 flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-slate-300" /></div>
            ) : selectedPatient ? (
              <>
                <div className="sticky top-0 bg-white border-b border-slate-100 px-6 py-4 z-10">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2.5">
                      <div className={`h-3 w-3 rounded-full ${STATUS_COLORS[selectedPatient.status]}`} />
                      <h2 className="font-bold text-slate-900 text-lg">{selectedPatient.firstName} {selectedPatient.lastName}</h2>
                    </div>
                    <div className="flex items-center gap-2">
                      {(role === "nurse" || role === "doctor") && showClinicalRecordLink && (
                        <a
                          href={`${BASE_URL}clinic/patients/${selectedPatient.id}`}
                          className="text-xs font-medium text-indigo-700 hover:text-indigo-900 px-2 py-1 rounded-lg hover:bg-indigo-50 transition-colors"
                        >
                          Open clinical record
                        </a>
                      )}
                      <button onClick={() => { setEditPatient(selectedPatient); setPatientError(""); }} className="text-slate-400 hover:text-slate-600 p-1.5 rounded-lg hover:bg-slate-100 transition-colors"><Pencil className="h-4 w-4" /></button>
                      <button onClick={() => handleDeletePatient(selectedPatient.id)} className="text-slate-400 hover:text-red-500 p-1.5 rounded-lg hover:bg-red-50 transition-colors"><Trash2 className="h-4 w-4" /></button>
                      <button onClick={() => setSelectedPatient(null)} className="text-slate-400 hover:text-slate-600 p-1.5 rounded-lg hover:bg-slate-100 transition-colors"><X className="h-5 w-5" /></button>
                    </div>
                  </div>
                </div>

                <div className="flex-1 p-6 space-y-6">
                  {/* Patient info */}
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div className="flex items-center gap-2 text-slate-600"><Calendar className="h-4 w-4 text-slate-400" /> DOB: {formatDate(selectedPatient.dateOfBirth)}</div>
                    <div className="flex items-center gap-2 text-slate-600"><Building2 className="h-4 w-4 text-slate-400" /> #{selectedPatient.hospitalNumber}</div>
                    {selectedPatient.diagnosis && <div className="flex items-center gap-2 text-slate-600 col-span-2"><User className="h-4 w-4 text-slate-400" /> {selectedPatient.diagnosis}</div>}
                    <div className="flex items-center gap-2 text-slate-400 col-span-2 text-xs"><span>RheumHub ID: {selectedPatient.rheumHubId}</span></div>
                  </div>
                  {selectedPatient.notes && (
                    <div className="bg-slate-50 rounded-xl px-4 py-3 text-sm text-slate-600">{selectedPatient.notes}</div>
                  )}

                  {/* Pre-consultation summary CTA */}
                  <Link
                    href={`/med-tracker/patients/${selectedPatient.id}/summary`}
                    className="flex items-center justify-between gap-3 rounded-xl border border-teal-200 bg-teal-50 hover:bg-teal-100 px-4 py-3 transition-colors group"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <ClipboardPen className="h-5 w-5 text-teal-700 shrink-0" />
                      <div className="min-w-0">
                        <p className="font-semibold text-sm text-teal-900">Pre-consultation summary</p>
                        <p className="text-xs text-teal-700">One-screen briefing before the appointment</p>
                      </div>
                    </div>
                    <ChevronRight className="h-5 w-5 text-teal-700 group-hover:translate-x-0.5 transition-transform" />
                  </Link>

                  {/* Medications */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-semibold text-slate-800 flex items-center gap-2"><Pill className="h-4 w-4 text-teal-600" /> Medications</h3>
                      <Button size="sm" onClick={() => { setShowAddMed(true); setMedError(""); }} className="bg-teal-700 hover:bg-teal-800 text-white gap-1 h-8 text-xs">
                        <Plus className="h-3.5 w-3.5" /> Add
                      </Button>
                    </div>
                    {selectedPatient.medications.length === 0 ? (
                      <div className="text-center py-8 text-slate-400 border border-dashed border-slate-200 rounded-xl">
                        <Pill className="h-8 w-8 mx-auto mb-2 opacity-30" />
                        <p className="text-sm">No medications added yet</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {selectedPatient.medications.map(med => {
                          const days = daysUntil(med.nextDueAt);
                          const isRed = days < 0;
                          const isAmber = days >= 0 && days <= alertDays;
                          const statusClass = !med.isActive ? "border-slate-100 bg-slate-50 opacity-60" : isRed ? "border-red-200 bg-red-50" : isAmber ? "border-amber-200 bg-amber-50" : "border-green-200 bg-green-50";
                          return (
                            <div key={med.id} className={`rounded-xl border p-3.5 ${statusClass}`}>
                              <div className="flex items-start justify-between gap-2">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className="font-semibold text-slate-900 text-sm">{med.medicationName}</span>
                                    {!med.isActive && <span className="text-xs bg-slate-200 text-slate-500 px-1.5 py-0.5 rounded-full">Inactive</span>}
                                    {med.medicationType && <span className="text-xs text-slate-500">{med.medicationType}</span>}
                                  </div>
                                  <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1 text-xs text-slate-500">
                                    {med.dose && <span>Dose: {med.dose}</span>}
                                    {med.frequency && <span>Freq: {med.frequency}</span>}
                                    {med.supplier && <span>Supplier: {med.supplier}</span>}
                                  </div>
                                  <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1 text-xs">
                                    {med.lastPrescribedAt && <span className="text-slate-500">Last prescribed: {formatDate(med.lastPrescribedAt)}</span>}
                                    <span className={`font-medium ${isRed ? "text-red-700" : isAmber ? "text-amber-700" : "text-green-700"}`}>
                                      Next due: {formatDate(med.nextDueAt)} ({daysLabel(days)})
                                    </span>
                                  </div>
                                  {med.notes && <p className="mt-1 text-xs text-slate-500 italic">{med.notes}</p>}
                                </div>
                                <div className="flex gap-1 shrink-0">
                                  <button onClick={() => { setEditMed(med); setMedError(""); }} className="text-slate-400 hover:text-slate-600 p-1 rounded hover:bg-white/60 transition-colors"><Pencil className="h-3.5 w-3.5" /></button>
                                  <button onClick={() => handleDeleteMed(med.id)} className="text-slate-400 hover:text-red-500 p-1 rounded hover:bg-white/60 transition-colors"><Trash2 className="h-3.5 w-3.5" /></button>
                                </div>
                              </div>
                              {med.isActive && (
                                <PrescriptionChecklist
                                  stages={selectedPatient.prescriptionStages ?? DEFAULT_STAGE_ORDER}
                                  steps={med.prescriptionSteps ?? []}
                                  onMark={(stage, notes) => handleMarkStep(med.id, stage, notes)}
                                  onUnmark={(stage) => handleUnmarkStep(med.id, stage)}
                                  onReset={() => handleResetCycle(med.id)}
                                />
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </>
            ) : null}
          </div>
        </div>
      )}

      {/* ── Modals ── */}
      {showImport && (
        <Modal title="Import patients from spreadsheet" onClose={() => setShowImport(false)} wide>
          <ImportModal
            onClose={() => setShowImport(false)}
            onCommitted={async () => { await loadPatients(); setShowImport(false); }}
          />
        </Modal>
      )}
      {showAddPatient && (
        <Modal title="Add patient" onClose={() => setShowAddPatient(false)}>
          <PatientForm onSave={handleAddPatient} onCancel={() => setShowAddPatient(false)} saving={patientSaving} error={patientError} />
        </Modal>
      )}
      {editPatient && (
        <Modal title="Edit patient" onClose={() => setEditPatient(null)}>
          <PatientForm
            initial={{ hospitalNumber: editPatient.hospitalNumber, firstName: editPatient.firstName, lastName: editPatient.lastName, dateOfBirth: editPatient.dateOfBirth, diagnosis: editPatient.diagnosis ?? "", notes: editPatient.notes ?? "" }}
            onSave={handleEditPatient} onCancel={() => setEditPatient(null)} saving={patientSaving} error={patientError}
          />
        </Modal>
      )}
      {showAddMed && (
        <Modal title="Add medication" onClose={() => setShowAddMed(false)} wide>
          <MedForm onSave={handleAddMed} onCancel={() => setShowAddMed(false)} saving={medSaving} error={medError} />
        </Modal>
      )}
      {editMed && (
        <Modal title="Edit medication" onClose={() => setEditMed(null)} wide>
          <MedForm
            initial={{ medicationName: editMed.medicationName, medicationType: editMed.medicationType ?? "", dose: editMed.dose ?? "", frequency: editMed.frequency ?? "", supplier: editMed.supplier ?? "", lastPrescribedAt: editMed.lastPrescribedAt ?? "", nextDueAt: editMed.nextDueAt, notes: editMed.notes ?? "", isActive: editMed.isActive }}
            onSave={handleEditMed} onCancel={() => setEditMed(null)} saving={medSaving} error={medError}
          />
        </Modal>
      )}
      {showSettings && (
        <Modal title="Alert settings" onClose={() => setShowSettings(false)}>
          <div className="space-y-5">
            <p className="text-sm text-slate-600">Set how far in advance you want to be alerted about upcoming prescription renewals. The traffic light turns amber when a prescription is within this window.</p>
            <div className="space-y-3">
              <Label className="text-sm font-medium">Alert me when prescriptions are due within:</Label>
              <div className="grid grid-cols-4 gap-2">
                {[7, 14, 21, 28].map(d => (
                  <button key={d} onClick={() => setSettingsDays(d)} className={`rounded-xl border py-2 text-sm font-medium transition-colors ${settingsDays === d ? "bg-teal-700 text-white border-teal-700" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>{d} days</button>
                ))}
              </div>
              <div className="flex items-center gap-3">
                <Label className="text-sm text-slate-600 whitespace-nowrap">Custom (days):</Label>
                <Input
                  type="number" min={1} max={365} value={settingsDays}
                  onChange={e => setSettingsDays(Math.max(1, Math.min(365, parseInt(e.target.value) || 1)))}
                  className="w-24"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowSettings(false)}>Cancel</Button>
              <Button onClick={handleSaveSettings} disabled={settingsSaving} className="bg-teal-700 hover:bg-teal-800 text-white">
                {settingsSaving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Save settings
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );

  const isAdvicelineHub = typeof window !== "undefined" && window.location.hostname.includes("advicelinehub");

  if (role === "pharmacist") {
    return <PharmacistLayout title="Medication Tracker">{content}</PharmacistLayout>;
  }
  if (role === "doctor") {
    return <DoctorLayout title="Medication Tracker">{content}</DoctorLayout>;
  }
  // Use ALH minimal layout on advicelinehub.co.uk, full RheumHub layout otherwise
  if (isAdvicelineHub) {
    return <AdvicelineNurseLayout title="Medication Tracker">{content}</AdvicelineNurseLayout>;
  }
  return <NurseLayout title="Medication Tracker">{content}</NurseLayout>;
}
